import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, ImagePlus, Film, FileText, Star, X, Trash2, Link as LinkIcon } from "lucide-react";

/**
 * Media management for a single admin-ingested master project.
 * - Image uploads → appended to image_urls; the FIRST becomes the Hero.
 * - Video upload → single video_url (replace-on-upload).
 * - Brochure upload → single brochure_url (replace-on-upload).
 *
 * Callback `onChange(updatedProject)` lets the parent refresh its recent-list
 * state without a round-trip. Falls back to a full reload if not provided.
 */
export default function MasterProjectMediaPanel({ project, onChange }) {
  const [images, setImages] = useState(project.image_urls || []);
  const [videoUrl, setVideoUrl] = useState(project.video_url || null);
  const [videoPoster, setVideoPoster] = useState(project.video_poster_url || null);
  const [videoProvider, setVideoProvider] = useState(project.video_provider || null);
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const [brochureUrl, setBrochureUrl] = useState(project.brochure_url || null);
  const [busy, setBusy] = useState(null); // "image" | "video" | "video-url" | "brochure" | null
  const imgRef = useRef(null);
  const vidRef = useRef(null);
  const broRef = useRef(null);

  const notify = (patch) => {
    if (onChange) onChange({ ...project, ...patch });
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name)) return toast.error("Only PNG / JPG / WEBP allowed");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image too large (max 5 MB)");
    setBusy("image");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/master-project/${project.project_id}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImages(data.image_urls);
      notify({ image_urls: data.image_urls });
      toast.success(images.length === 0 ? "Uploaded — this is now the Hero image" : "Added to gallery");
      if (imgRef.current) imgRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image upload failed");
    } finally {
      setBusy(null);
    }
  };

  const removeImage = async (url) => {
    if (!window.confirm("Remove this image from the project?")) return;
    setBusy("image");
    try {
      const { data } = await api.delete(`/admin/master-project/${project.project_id}/image`, { params: { url } });
      setImages(data.image_urls);
      notify({ image_urls: data.image_urls });
      toast.success("Image removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    if (!/\.(mp4|webm|mov)$/i.test(file.name)) return toast.error("Only MP4 / WEBM / MOV allowed");
    if (file.size > 50 * 1024 * 1024) return toast.error("Video too large (max 50 MB)");
    setBusy("video");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/master-project/${project.project_id}/video`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideoUrl(data.video_url);
      setVideoPoster(null);
      setVideoProvider(null);
      notify({ video_url: data.video_url, video_poster_url: null, video_provider: null });
      toast.success("Video uploaded");
      if (vidRef.current) vidRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Video upload failed");
    } finally {
      setBusy(null);
    }
  };

  const saveVideoUrl = async () => {
    const url = videoUrlInput.trim();
    if (!url) return toast.error("Paste a YouTube / Vimeo / video URL first");
    if (!/^https?:\/\//i.test(url)) return toast.error("URL must start with http:// or https://");
    setBusy("video-url");
    try {
      const { data } = await api.post(`/admin/master-project/${project.project_id}/video-url`, { url });
      setVideoUrl(data.video_url);
      setVideoPoster(data.video_poster_url);
      setVideoProvider(data.video_provider);
      notify({ video_url: data.video_url, video_poster_url: data.video_poster_url, video_provider: data.video_provider });
      setVideoUrlInput("");
      toast.success(
        data.video_provider === "youtube" ? "YouTube video linked (thumbnail auto-detected)"
        : data.video_provider === "vimeo" ? "Vimeo video linked"
        : "External video URL saved",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save video URL");
    } finally {
      setBusy(null);
    }
  };

  const removeVideo = async () => {
    if (!window.confirm("Remove the project video?")) return;
    setBusy("video");
    try {
      await api.delete(`/admin/master-project/${project.project_id}/video`);
      setVideoUrl(null);
      setVideoPoster(null);
      setVideoProvider(null);
      notify({ video_url: null, video_poster_url: null, video_provider: null });
      toast.success("Video removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  const uploadBrochure = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) return toast.error("Brochure must be a PDF");
    if (file.size > 10 * 1024 * 1024) return toast.error("Brochure too large (max 10 MB)");
    setBusy("brochure");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/master-project/${project.project_id}/brochure`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBrochureUrl(data.brochure_url);
      notify({ brochure_url: data.brochure_url });
      toast.success("Brochure uploaded");
      if (broRef.current) broRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brochure upload failed");
    } finally {
      setBusy(null);
    }
  };

  const removeBrochure = async () => {
    if (!window.confirm("Remove the brochure PDF?")) return;
    setBusy("brochure");
    try {
      await api.delete(`/admin/master-project/${project.project_id}/brochure`);
      setBrochureUrl(null);
      notify({ brochure_url: null });
      toast.success("Brochure removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-3 border-t border-sg-line pt-3 space-y-3" data-testid={`media-panel-${project.project_id}`}>
      {/* -------- Images -------- */}
      <div>
        <div className="text-[11px] font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
          <ImagePlus className="w-3 h-3" /> Images {images.length > 0 && <span className="font-mono">· {images.length}</span>}
        </div>
        {images.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mb-2" data-testid={`media-gallery-${project.project_id}`}>
            {images.map((url, i) => (
              <div key={url} className="relative group aspect-square rounded-lg overflow-hidden bg-sg-cream-soft border border-sg-line" data-testid={`media-thumb-${project.project_id}-${i}`}>
                <img src={url} alt={`Project ${i + 1}`} className="w-full h-full object-cover" />
                {i === 0 && (
                  <div className="absolute top-1 left-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sg-gold-deep text-white text-[9px] font-semibold uppercase tracking-wide" data-testid={`media-hero-badge-${project.project_id}`}>
                    <Star className="w-2.5 h-2.5" /> Hero
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(url)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove image"
                  data-testid={`media-remove-image-${project.project_id}-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="inline-flex items-center gap-1.5 text-xs text-sg-gold-deep hover:text-sg-navy cursor-pointer">
          <input
            ref={imgRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => uploadImage(e.target.files?.[0])}
            data-testid={`media-image-input-${project.project_id}`}
          />
          {busy === "image" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {busy === "image" ? "Uploading…" : (images.length === 0 ? "Add Hero image" : "Add another image")}
        </label>
      </div>

      {/* -------- Video -------- */}
      <div>
        <div className="text-[11px] font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
          <Film className="w-3 h-3" /> Video
        </div>
        {videoUrl ? (
          <div className="flex items-center gap-2 mb-2" data-testid={`media-video-present-${project.project_id}`}>
            {videoProvider ? (
              // External URL — show poster / provider badge
              videoPoster ? (
                <img src={videoPoster} alt="video poster" className="w-16 h-10 rounded object-cover bg-black" />
              ) : (
                <div className="w-16 h-10 rounded bg-sg-navy text-white flex items-center justify-center">
                  <Film className="w-4 h-4" />
                </div>
              )
            ) : (
              <video src={videoUrl} className="w-16 h-10 rounded object-cover bg-black" muted playsInline />
            )}
            <div className="flex-1 min-w-0">
              {videoProvider && (
                <span className="inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-sg-gold/20 text-sg-gold-deep border border-sg-gold/40 mr-1.5" data-testid={`media-video-provider-${project.project_id}`}>
                  {videoProvider}
                </span>
              )}
              <a href={videoUrl} target="_blank" rel="noreferrer" className="text-xs text-sg-navy underline underline-offset-2 hover:text-sg-gold-deep truncate font-mono">
                {videoProvider ? videoUrl : videoUrl.split("/").pop()}
              </a>
            </div>
            <button type="button" onClick={removeVideo} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1 shrink-0" data-testid={`media-remove-video-${project.project_id}`}>
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ) : (
          <div className="text-xs text-sg-mute italic mb-2">No video attached</div>
        )}

        {/* External URL input row */}
        <div className="flex items-center gap-2 mb-2" data-testid={`media-video-url-row-${project.project_id}`}>
          <input
            type="url"
            value={videoUrlInput}
            onChange={(e) => setVideoUrlInput(e.target.value)}
            placeholder="Paste YouTube / Vimeo URL"
            className="flex-1 min-w-0 h-8 rounded-md border border-sg-line bg-white text-xs px-2 focus:outline-none focus:ring-2 focus:ring-sg-gold/40 font-mono"
            data-testid={`media-video-url-input-${project.project_id}`}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveVideoUrl(); } }}
          />
          <button
            type="button"
            onClick={saveVideoUrl}
            disabled={busy === "video-url" || !videoUrlInput.trim()}
            className="h-8 rounded-md bg-sg-navy hover:bg-sg-navy-700 text-white text-xs font-semibold px-3 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            data-testid={`media-video-url-save-${project.project_id}`}
          >
            {busy === "video-url" ? <Loader2 className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />}
            Save URL
          </button>
        </div>
        <div className="text-[10px] text-sg-mute mb-1.5">
          Or upload a local file (URL takes priority when both are set):
        </div>

        <label className="inline-flex items-center gap-1.5 text-xs text-sg-gold-deep hover:text-sg-navy cursor-pointer">
          <input
            ref={vidRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => uploadVideo(e.target.files?.[0])}
            data-testid={`media-video-input-${project.project_id}`}
          />
          {busy === "video" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {busy === "video" ? "Uploading…" : (videoUrl ? "Replace with a file" : "Upload video file")}
        </label>
      </div>

      {/* -------- Brochure -------- */}
      <div>
        <div className="text-[11px] font-semibold text-sg-mute uppercase tracking-wide flex items-center gap-1.5 mb-1.5">
          <FileText className="w-3 h-3" /> Brochure (PDF)
        </div>
        {brochureUrl ? (
          <div className="flex items-center gap-2 mb-1" data-testid={`media-brochure-present-${project.project_id}`}>
            <a href={brochureUrl} target="_blank" rel="noreferrer" className="text-xs text-sg-navy underline underline-offset-2 hover:text-sg-gold-deep truncate flex-1 font-mono">
              {brochureUrl.split("/").pop()}
            </a>
            <button type="button" onClick={removeBrochure} className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1" data-testid={`media-remove-brochure-${project.project_id}`}>
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ) : (
          <div className="text-xs text-sg-mute italic mb-1">No brochure uploaded</div>
        )}
        <label className="inline-flex items-center gap-1.5 text-xs text-sg-gold-deep hover:text-sg-navy cursor-pointer">
          <input
            ref={broRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => uploadBrochure(e.target.files?.[0])}
            data-testid={`media-brochure-input-${project.project_id}`}
          />
          {busy === "brochure" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {busy === "brochure" ? "Uploading…" : (brochureUrl ? "Replace brochure" : "Upload brochure PDF")}
        </label>
      </div>
    </div>
  );
}
